import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { contasApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { formatMoney } from '../../lib/utils'
import { hojeISO, lerValor, primeiroDoMes, useDebounced } from '../../lib/corte'
import { Divergencias, PreviaManual } from './PreviaSaldo'
import type { FinancialAccount } from '../../types'

/** Conta manual: define o saldo inicial e a data de corte; o que for anterior vira histórico. */
export function SaldoAberturaModal({ conta, onClose }: { conta: FinancialAccount; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [data, setData] = useState(conta.dataAbertura?.slice(0, 10) ?? primeiroDoMes())
  const [saldoTxt, setSaldoTxt] = useState(conta.balance.toFixed(2))
  const [entendi, setEntendi] = useState(false)

  const saldo = lerValor(saldoTxt)
  const dataD = useDebounced(data)
  const saldoD = useDebounced(saldo)
  const valida = /^\d{4}-\d{2}-\d{2}$/.test(data) && data <= hojeISO()
  const validaD = /^\d{4}-\d{2}-\d{2}$/.test(dataD) && dataD <= hojeISO()

  const previaQ = useQuery({
    queryKey: ['previa-saldo-abertura', conta.id, dataD, saldoD],
    queryFn: () => contasApi.previaSaldoAbertura(conta.id, { dataCorte: dataD, saldo: saldoD! }),
    enabled: validaD && saldoD != null,
    placeholderData: (anterior) => anterior,
  })
  const previa = previaQ.data
  const calculando = previaQ.isFetching || data !== dataD || saldo !== saldoD

  const salvar = useMutation({
    mutationFn: () => contasApi.definirSaldoAbertura(conta.id, { dataCorte: data, saldo: saldo!, confirmar: true }),
    onSuccess: (r) => {
      for (const k of ['contas', 'transacoes', 'cartoes', 'dashboard', 'pluggy-vinculos', 'onboarding']) qc.invalidateQueries({ queryKey: [k] })
      toast.success(`Saldo inicial definido. Saldo final: ${formatMoney(r.saldoFinal)}.`)
      onClose()
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível definir o saldo inicial.'),
  })

  const precisaConfirmar = !!previa?.exigeConfirmacao
  const pode = valida && saldo != null && !!previa && !calculando && (!precisaConfirmar || entendi)

  return (
    <Modal
      open
      onClose={salvar.isPending ? () => {} : onClose}
      title={`Saldo inicial e corte · ${conta.name}`}
      footer={
        <>
          <Button variant="light" onClick={onClose} disabled={salvar.isPending}>Cancelar</Button>
          <Button data-tutorial="corte.confirmar" icon="ri-save-line" onClick={() => salvar.mutate()} loading={salvar.isPending} disabled={!pode}>Definir saldo inicial</Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-13 text-muted">
          Escolha o dia do corte e o saldo que a conta tem hoje. O que for anterior ao corte vira histórico: continua na lista e nos relatórios, mas deixa de mexer no saldo.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input data-tutorial="corte.data" label="Data do corte" type="date" max={hojeISO()} value={data} onChange={(e) => { setData(e.target.value); setEntendi(false) }} />
          <Input data-tutorial="corte.saldo" label="Saldo de hoje (R$)" type="number" step="0.01" value={saldoTxt} onChange={(e) => { setSaldoTxt(e.target.value); setEntendi(false) }} />
        </div>
        {data > hojeISO() && <p className="text-13 text-expense" role="alert">A data de corte não pode ser futura.</p>}
        {previa ? (
          <div className={calculando ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <PreviaManual previa={previa} />
          </div>
        ) : (
          <p className="text-13 text-muted">{calculando ? 'Calculando...' : 'Informe a data e o saldo para ver o efeito.'}</p>
        )}
        {previa && <Divergencias lista={previa.divergencias} />}
        {previaQ.isError && <p className="text-13 text-expense" role="alert">{(previaQ.error as Error).message || 'Não foi possível calcular a prévia.'}</p>}
        {precisaConfirmar && (
          <div className="space-y-2 rounded border border-warning/50 bg-warning/10 p-3 text-13 text-warning">
            <p><i className="ri-error-warning-line" aria-hidden /> Esta conta já tem lançamentos. {previa!.lancamentosHistorico} anteriores ao corte viram histórico e o saldo passa de {formatMoney(previa!.saldoAtual)} para {formatMoney(previa!.saldoNovo)}.</p>
            <label className="flex cursor-pointer items-center gap-2 text-white">
              <input type="checkbox" checked={entendi} onChange={(e) => setEntendi(e.target.checked)} />
              Entendi o efeito e quero continuar
            </label>
          </div>
        )}
      </div>
    </Modal>
  )
}
